import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useWorkouts, useProfile } from '../hooks/useProfile';
import { dayjs } from '../lib/utils';
import { useMonthlyRunningTotals } from '../hooks/useMonthlyRunningTotals';
import Loader from '../pages/Loader';
import { ConnectionPrompt } from './ConnectionPrompt';
import WorkoutCard from './WorkoutCard';
import PageHeader from './PageHeader';
import { Plus } from 'lucide-react';

const PAGE_SIZE = 10;

const Workouts = () => {
  const {
    data: user,
    isFetching: userFetching,
    isLoading: userLoading,
  } = useProfile();
  const {
    data: workoutData,
    isFetching: workoutFetching,
    isLoading: workoutLoading,
  } = useWorkouts();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => navigate({ to: '/workouts/log' });
    window.addEventListener('openLogWorkoutModal', handler);
    return () => window.removeEventListener('openLogWorkoutModal', handler);
  }, [navigate]);

  const allWorkouts = workoutData?.workouts || [];
  const connected = Boolean(user?.user?.connectionId || user?.user?.connections?.length);
  const types = [...new Set(allWorkouts.map((workout) => workout.type).filter(Boolean))];
  const workouts = allWorkouts.filter((workout) =>
    (!query || `${workout.title || ''} ${workout.type || ''}`.toLowerCase().includes(query.toLowerCase())) &&
    (typeFilter === 'all' || workout.type === typeFilter),
  );
  const monthlyRunningTotals = useMonthlyRunningTotals(workouts);
  const hasMore = visibleCount < workouts.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (userLoading || workoutLoading) {
    return <Loader />;
  }

  if (!connected) {
    return <ConnectionPrompt />;
  }

  if (connected && workoutData?.workouts?.length === 0) {
    return (
      <div className="surface-card flex min-h-[50vh] flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          No workouts found.
        </h2>
        <p className="text-sm text-gray-500 mb-6 text-center">
          Connect your Strava or Garmin account to import workouts and get
          started.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-5xl">
        <PageHeader
          eyebrow="Your training history"
          title="Activities"
          description="Review every run, ride, and session in one focused timeline."
          action={
            <button
              type="button"
              className="button-primary"
              onClick={() => navigate({ to: '/workouts/log' })}
            >
              <Plus className="h-4 w-4" />
              Log workout
            </button>
          }
        />
        <div className="flex-col">
          <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white/70 p-4 sm:flex-row dark:border-slate-700 dark:bg-brand-midnight/50">
            <label className="flex-1 text-sm font-semibold text-brand-midnight dark:text-white">
              Search activities
              <input value={query} onChange={(event) => { setQuery(event.target.value); setVisibleCount(PAGE_SIZE); }} placeholder="Run, strength, recovery..." className="mt-1 min-h-10 w-full rounded-lg border border-gray-200 bg-white px-3 font-normal outline-none focus:border-brand-cobalt dark:border-slate-600 dark:bg-slate-900" />
            </label>
            <label className="text-sm font-semibold text-brand-midnight dark:text-white">
              Activity type
              <select value={typeFilter} onChange={(event) => { setTypeFilter(event.target.value); setVisibleCount(PAGE_SIZE); }} className="mt-1 min-h-10 w-full rounded-lg border border-gray-200 bg-white px-3 font-normal dark:border-slate-600 dark:bg-slate-900 sm:w-44">
                <option value="all">All activities</option>
                {types.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
          </div>
          {workoutLoading ||
          userLoading ||
          (workoutFetching && visibleCount === PAGE_SIZE) ||
          (userFetching && visibleCount === PAGE_SIZE) ? (
            <div className="flex justify-center items-center min-h-[50vh]">
              <Loader />
            </div>
          ) : (
            <div className="space-y-5 px-1 md:px-0 md:space-y-6">
              {(() => {
                const visible = workouts.slice(0, visibleCount);
                const grouped: { monthKey: string; workouts: any[] }[] = [];
                for (const w of visible) {
                  const key = dayjs(w.completedAt).format('MMMM YYYY');
                  const last = grouped[grouped.length - 1];
                  if (last && last.monthKey === key) {
                    last.workouts.push(w);
                  } else {
                    grouped.push({ monthKey: key, workouts: [w] });
                  }
                }
                return grouped.map((group) => (
                  <div key={group.monthKey}>
                    <div className="activity-month-header sticky top-0 z-[1100] -mx-1 flex items-end justify-between px-3 py-3 md:mx-auto md:px-0">
                      <span className="inline-flex rounded-full bg-brand-greenSoft px-3 py-1 text-base font-extrabold text-brand-midnight ring-1 ring-brand-volt/30 dark:bg-brand-volt dark:text-brand-midnight">
                        {group.monthKey}
                      </span>
                      <span className="text-2xl font-black tracking-[-0.03em] text-brand-midnight">
                         <span className="text-xs font-semibold uppercase tracking-wide">Running</span>{' '}
                         {(monthlyRunningTotals[group.monthKey] || 0).toFixed(2)} mi
                      </span>
                    </div>
                    <div className="space-y-3 pt-3">
                      {group.workouts.map((workout) => (
                        <div key={workout.id} className="w-full px-0">
                          <WorkoutCard
                            workout={workout}
                            onClick={() =>
                              navigate({
                                to: '/workouts/$id',
                                params: { id: workout.id },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
              {hasMore ? (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  <Loader />
                </div>
              ) : <p className="py-5 text-center text-sm text-gray-500" role="status">You are caught up.</p>}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Workouts;
